import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";

// Custom Hooks & State
import { useAuth } from "@/state/auth";
import { useVerify } from "@/state/verify";
import { useTranslation } from "@/hooks/useTranslation";
import type { TFunction } from "i18next";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { ParticleNetwork } from "@/components/ui/particle-network";

// =============================================================================
// TYPES & CONSTANTS
// =============================================================================

interface VerificationStep {
  id: 1 | 2;
  title: string;
  header: React.ReactNode;
  footer?: React.ReactNode;
  action: (payload: string) => void;
}

interface StepStates {
  emailVerified: boolean;
  telegramLinked: boolean;
}

interface StatusFrameProps {
  onStepClick: (id: VerificationStep['id']) => void;
  stepStates: StepStates;
  verificationSteps: VerificationStep[];
}

interface VerifyFormProps {
  onBack: () => void;
  step: VerificationStep;
  isPending?: boolean;
  error?: string;
}

const slideVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction < 0 ? 100 : -100, opacity: 0 })
};

const slideTransition = {
  x: { type: "tween" as const, duration: 0.1 },
  opacity: { duration: 0.3 }
};

// =============================================================================
// STATUS FRAME COMPONENT
// =============================================================================

const getIsStepCompleted = (stepId: VerificationStep['id'], states: StepStates): boolean => {
  switch (stepId) {
    case 1: return states.emailVerified;
    case 2: return states.telegramLinked;
    default: return false;
  }
};

const StatusFrame: React.FC<StatusFrameProps> = ({ onStepClick, stepStates, verificationSteps }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const completedCount = Object.values(stepStates).filter(Boolean).length;
  const totalSteps = verificationSteps.length;
  const allStepsCompleted = completedCount === totalSteps;

  return (
    <motion.div
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      custom={-1}
      transition={slideTransition}
      className="w-full"
    >
      <h1 className="text-2xl font-bold mb-6 text-white">
        {t('pages.auth.completeRegister.title')}
      </h1>

      <div className="mb-6">
        <p className="text-[#0dcbfc] text-3xl font-medium">
          {completedCount} / {totalSteps}
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {verificationSteps.map((step) => {
          const isCompleted = getIsStepCompleted(step.id, stepStates);

          return (
            <div
              key={step.id}
              onClick={() => !isCompleted && onStepClick(step.id)}
              className={`
                px-5 py-4 select-none transition-colors duration-200
                flex items-center justify-between rounded-2xl
                border border-[#0dcbfc]/20 bg-[#080c15]/50
                ${!isCompleted
                  ? 'cursor-pointer hover:bg-[#0dcbfc]/10 hover:border-[#0dcbfc]/40'
                  : 'cursor-default opacity-70'
                }
              `}
            >
              <h2 className="text-base font-semibold text-white">{step.title}</h2>
              {isCompleted ? (
                <Check size={20} className="text-green-400" />
              ) : (
                <ArrowRight size={20} className="text-[#0dcbfc]/60" />
              )}
            </div>
          );
        })}
      </div>

      <Button
        disabled={!allStepsCompleted}
        onClick={() => navigate({ to: '/dashboard' })}
        className="w-full bg-[#0dcbfc] hover:bg-[#0dcbfc]/90 text-[#080c15] font-semibold shadow-[0_0_20px_-5px_rgba(13,203,252,0.4)]"
      >
        {t('pages.auth.completeRegister.goToDashboard')}
      </Button>
    </motion.div>
  );
};

// =============================================================================
// VERIFY FORM COMPONENT
// =============================================================================

const VerifyForm: React.FC<VerifyFormProps> = ({ onBack, step, isPending = false, error }) => {
  const [input, setInput] = useState("");
  const { t } = useTranslation();

  const isInputValid = input.length === 6;

  const handleSubmit = () => {
    if (isInputValid && step.action) {
      step.action(input);
    }
  };

  const getChannelName = (t: TFunction) => step.id === 2
    ? t('pages.auth.completeRegister.telegram')
    : t('pages.auth.completeRegister.emailAddress');

  return (
    <motion.div
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      custom={1}
      transition={slideTransition}
      className="w-full"
    >
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="mr-3 p-1 hover:bg-[#0dcbfc]/10 rounded transition-colors duration-200"
          disabled={isPending}
        >
          <ArrowRight size={20} className="rotate-180 text-[#0dcbfc]/60" />
        </button>
        <h1 className="text-2xl font-bold text-white">{step.title}</h1>
      </div>

      <div className="space-y-6 select-none">
        <div className="text-start">
          {step.header}
          <p className="text-[#0dcbfc]/60 text-sm mt-4">
            {t('pages.auth.completeRegister.enterCode', { channel: getChannelName(t) })}
          </p>
        </div>

        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={input}
            onChange={setInput}
            disabled={isPending}
          >
            <InputOTPGroup>
              {[0, 1, 2].map(i => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              {[3, 4, 5].map(i => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!isInputValid || isPending}
          className="w-full bg-[#0dcbfc] hover:bg-[#0dcbfc]/90 text-[#080c15] font-semibold shadow-[0_0_20px_-5px_rgba(13,203,252,0.4)]"
        >
          {isPending ? t('common.messages.loading') : t('pages.auth.completeRegister.verify')}
        </Button>

        {error && (
          <p className="text-red-400 text-sm text-center">{String(error)}</p>
        )}

        <div className="text-center">
          {step.footer}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const CompleteRegisterPage: React.FC = () => {
  const { toast } = useToast()

  const loadingFromQuery = new URLSearchParams(window.location.search).get('loading') === 'true';

  const {
    requestEmail,
    verifyEmail,
    verifyTelegram,
    verifyEmailPending,
    verifyEmailSuccess,
    verifyEmailError,
    verifyTelegramPending,
    verifyTelegramSuccess,
    verifyTelegramError,
  } = useVerify();
  const { user, dataIsLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [currentView, setCurrentView] = useState<'status' | 'verify'>('status');
  const [stepId, setStepId] = useState<VerificationStep['id']>(1);
  const [direction, setDirection] = useState(0);

  const stepStates: StepStates = useMemo(() => ({
    emailVerified: !!user?.verified,
    telegramLinked: !!user?.uid
  }), [user]);

  const allStepsCompleted = stepStates.emailVerified && stepStates.telegramLinked;

  const getVerificationSteps = useCallback((t: TFunction): VerificationStep[] => [
    {
      id: 1,
      title: t('pages.auth.completeRegister.emailVerification'),
      header: (<h2 className="text-lg font-semibold mb-2 text-white">{t('pages.auth.completeRegister.emailVerification')}</h2>
      ),
      footer: (
        <button
          onClick={() => {
            requestEmail()
            toast({
              title: "Code Sent",
              description: "Check your email, Please allow up to 5 minutes before you retry.",
              duration: 5000,
              variant: 'default'
            })
          }}
          className="text-sm text-[#0dcbfc]/60 hover:text-[#0dcbfc] transition-colors duration-200"
        >
          {t('pages.auth.completeRegister.resendCode')}
        </button>
      ),
      action: (code: string) => verifyEmail(code),
    },
    {
      id: 2,
      title: t('pages.auth.completeRegister.telegramVerification'),
      header: (
        <p className="text-[#0dcbfc]/60 text-sm mb-2 font-medium text-start">
          <a
            className="text-[#0dcbfc] bg-[#080c15] rounded-lg px-2 py-1 hover:underline"
            href="https://t.me/nexa_academy_bot"
            target="_blank"
            rel="noopener noreferrer"
          >
            /get_code
          </a>
          {' '}from our{' '}
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 rounded-md">
            official group
          </span>
        </p>
      ),
      action: (code: string) => verifyTelegram(code),
    }
  ], [requestEmail, verifyEmail, verifyTelegram, toast]);

  const verificationSteps = useMemo(() => getVerificationSteps(t), [t, getVerificationSteps]);

  const currentVerificationStep = useMemo(() =>
    verificationSteps.find(step => step.id === stepId),
    [stepId, verificationSteps]
  );

  useEffect(() => {
    if (allStepsCompleted) {
      navigate({ to: '/dashboard', replace: true });
    }
  }, [allStepsCompleted, navigate]);

  const handleViewChange = useCallback((newView: 'status' | 'verify') => {
    if (newView !== currentView) {
      setDirection(newView === 'verify' ? 1 : -1);
      setCurrentView(newView);
    }
  }, [currentView]);

  const handleStepClick = useCallback((clickedStepId: VerificationStep['id']) => {
    const isCompleted = getIsStepCompleted(clickedStepId, stepStates);
    if (!isCompleted) {
      setStepId(clickedStepId);
      handleViewChange('verify');
    }
  }, [handleViewChange, stepStates]);

  useEffect(() => {
    if (verifyEmailSuccess) {
      handleViewChange('status');
    }
  }, [verifyEmailSuccess, handleViewChange]);

  useEffect(() => {
    if (verifyTelegramSuccess) {
      handleViewChange('status');
    }
  }, [verifyTelegramSuccess, handleViewChange]);

  const getMutationState = (id: VerificationStep['id']) => {
    switch (id) {
      case 1: return { isPending: verifyEmailPending, error: verifyEmailError };
      case 2: return { isPending: verifyTelegramPending, error: verifyTelegramError };
      default: return { isPending: false, error: undefined };
    }
  };

  const currentMutationState = currentVerificationStep ? getMutationState(currentVerificationStep.id) : { isPending: false, error: undefined };

  if (dataIsLoading || loadingFromQuery) {
    return (
      <div className="relative min-h-screen w-full flex justify-center items-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <ParticleNetwork className="w-full h-full" />
        </div>
        <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />

        <div className="relative z-10">
          <Card className="p-8 max-w-md w-full bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md">
            <CardContent className="p-0">
              <p className="rounded-lg bg-[#080c15]/50 p-4 justify-center flex items-center text-white text-lg font-medium animate-pulse">
                Nexa is {t('common.messages.loading')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!currentVerificationStep && currentView === 'verify') {
    handleViewChange('status');
    return null;
  }

  return (
    <div className="relative min-h-screen w-full flex justify-center items-center px-6 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <ParticleNetwork className="w-full h-full" />
      </div>

      <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />

      <Toaster />

      <div className="relative z-10">
        <Card className="p-4 md:p-8 max-w-md w-full bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md shadow-[0_0_50px_-12px_rgba(13,203,252,0.25)] relative overflow-hidden transition-all duration-300">
          <AnimatePresence mode="wait" custom={direction}>
            {currentView === 'status' ? (
              <StatusFrame
                key="status"
                onStepClick={handleStepClick}
                stepStates={stepStates}
                verificationSteps={verificationSteps}
              />
            ) : (
              <VerifyForm
                key="verify"
                onBack={() => handleViewChange('status')}
                step={currentVerificationStep!}
                isPending={currentMutationState.isPending}
                error={currentMutationState.error?.message}
              />
            )}
          </AnimatePresence>
        </Card>
      </div>
    </div>
  );
};
